import {
  CommandBarViewParams,
  CommandBarTreeViewParams,
  CommandingSurfaceViewParams,
  CommandingSurfaceTreeViewParams,
  CommandProps,
  ShowCommandingSurfaceData,
  ShowCommandingSurfaceTreeData
} from '@fluidx/office-fluid-types';

/** TODO: Task #5331007 Right now these converters are needed to ensure that when data related to the commanding
 *  surface is accessed no matter if the host is using the 2D array data format or the tree format it will all be
 *  translated and work together. Eventually only the tree version of the data should be used and when it is then
 *  these converters should be unneeded and removed. */

/** Given some version of CommandBarViewParams convert to and return the tree version */
export function getCommandBarTreeViewParams(
  commandBarViewParams: CommandBarViewParams | CommandBarTreeViewParams
): CommandBarTreeViewParams {
  if (!isInstanceOfCommandBarTreeViewParams(commandBarViewParams)) {
    return {
      ...commandBarViewParams,
      mainBarCommands: convertCommandPropsObjectArrayFormatToSingleArrayTree(commandBarViewParams.mainBarCommands),
      otherCommands: convertCommandPropsObjectArrayFormatToSingleArrayTree(commandBarViewParams.otherCommands)
    };
  }
  return commandBarViewParams;
}

/** Given some version of CommandBarViewParams convert to and return the object array version */
export function getCommandBarArrayViewParams(
  commandBarViewParams: CommandBarViewParams | CommandBarTreeViewParams
): CommandBarViewParams {
  if (isInstanceOfCommandBarTreeViewParams(commandBarViewParams)) {
    return {
      ...commandBarViewParams,
      mainBarCommands: convertCommandPropsSingleArrayTreeToObjectArrayFormat(commandBarViewParams.mainBarCommands),
      otherCommands: convertCommandPropsSingleArrayTreeToObjectArrayFormat(commandBarViewParams.otherCommands)
    };
  }
  return commandBarViewParams;
}

/** Given some version of CommandingSurfaceViewParams convert to and return the Tree version */
export function getCommandingSurfaceTreeViewParams(
  commandSurfaceViewParams: CommandingSurfaceViewParams | CommandingSurfaceTreeViewParams
): CommandingSurfaceTreeViewParams {
  if (isInstanceOfCommandingSurfaceTreeViewParams(commandSurfaceViewParams)) {
    return commandSurfaceViewParams;
  }
  return {
    ...commandSurfaceViewParams,
    topBarCommands: convertCommandPropsObjectArrayFormatToSingleArrayTree(commandSurfaceViewParams.topBarCommands),
    mainBodyCommands: convertCommandPropsObjectArrayFormatToSingleArrayTree(commandSurfaceViewParams.mainBodyCommands)
  };
}

/** Given some version of CommandingSurfaceViewParams convert to and return the object array version */
export function getCommandingSurfaceArrayViewParams(
  commandSurfaceViewParams: CommandingSurfaceViewParams | CommandingSurfaceTreeViewParams
): CommandingSurfaceViewParams {
  if (isInstanceOfCommandingSurfaceTreeViewParams(commandSurfaceViewParams)) {
    return {
      ...commandSurfaceViewParams,
      topBarCommands: convertCommandPropsSingleArrayTreeToObjectArrayFormat(commandSurfaceViewParams.topBarCommands),
      mainBodyCommands: convertCommandPropsSingleArrayTreeToObjectArrayFormat(commandSurfaceViewParams.mainBodyCommands)
    };
  }
  return commandSurfaceViewParams;
}

/** Given some version of ShowCommandingSurfaceData convert to and return the Tree version */
export function getShowCommandingSurfaceTreeData(
  showCommandingSurfaceData: ShowCommandingSurfaceData | ShowCommandingSurfaceTreeData
): ShowCommandingSurfaceTreeData {
  if (!isInstanceOfShowCommandingSurfaceTreeData(showCommandingSurfaceData)) {
    return {
      ...showCommandingSurfaceData,
      topBarCommands: convertCommandPropsDoubleArrayToSingleWithTree(showCommandingSurfaceData.topBarCommands)!,
      commandGroups: convertCommandPropsDoubleArrayToSingleWithTree(showCommandingSurfaceData.commandGroups)!
    };
  }
  return showCommandingSurfaceData;
}

/** Given a function that returns CommandProps in the tree structure convert to and return
 *  a function that returns CommandProps in the double array structure */
export function getCommandPropsDoubleArrayFunctionFromSingle(
  currentFunction: () => CommandProps[]
): () => CommandProps[][] {
  return () => {
    const tempCommands: CommandProps[][] = [];
    currentFunction().forEach((command) => {
      if (command.subMenuCommands) {
        tempCommands.push(command.subMenuCommands);
      } else {
        tempCommands.push([command]);
      }
    });

    return tempCommands;
  };
}

/** Given a function that returns CommandProps in the double array structure convert to
 *  and return a function that returns CommandProps in the tree structure */
export function getCommandPropsSingleArrayFunctionFromDouble(
  currentFunction: (() => CommandProps[][]) | undefined
): (() => CommandProps[]) | undefined {
  if (!currentFunction) {
    return undefined;
  }
  return () => {
    return convertCommandPropsDoubleArrayToSingleWithTree(currentFunction());
  };
}

export function convertCommandPropsDoubleArrayToSingleWithTree(oldCommandGroup: CommandProps[][]): CommandProps[] {
  return oldCommandGroup.map((individualGroup, index) => {
    return {
      displayName: '',
      id: '' + index,
      subMenuCommands: individualGroup,
      isInlineSubMenu: true
    };
  });
}

export function convertCommandPropsObjectArrayFormatToSingleArrayTree(
  oldCommandGroups: { commands: CommandProps[] }[] | undefined
): CommandProps[] | undefined {
  if (!oldCommandGroups) {
    return undefined;
  }
  return oldCommandGroups.map((individualGroup, index) => {
    return {
      displayName: '',
      id: '' + index,
      subMenuCommands: individualGroup.commands,
      isInlineSubMenu: true
    };
  });
}

function convertCommandPropsSingleArrayTreeToObjectArrayFormat(
  newCommandGroups: CommandProps[] | undefined
): { commands: CommandProps[] }[] | undefined {
  if (!newCommandGroups) {
    return undefined;
  }
  return newCommandGroups.map((individualCommand) => {
    if (individualCommand.subMenuCommands) {
      return { commands: individualCommand.subMenuCommands };
    }
    return { commands: [individualCommand] };
  });
}

function isInstanceOfCommandBarTreeViewParams(
  object: CommandBarViewParams | CommandBarTreeViewParams
): object is CommandBarTreeViewParams {
  if (object.mainBarCommands && object.mainBarCommands[0]) {
    return 'displayName' in object.mainBarCommands[0];
  }
  if (object.otherCommands && object.otherCommands[0]) {
    return 'displayName' in object.otherCommands[0];
  }
  return false;
}

function isInstanceOfCommandingSurfaceTreeViewParams(
  object: CommandingSurfaceViewParams | CommandingSurfaceTreeViewParams
): object is CommandingSurfaceTreeViewParams {
  if (object.topBarCommands && object.topBarCommands[0]) {
    return 'displayName' in object.topBarCommands[0];
  }
  if (object.mainBodyCommands && object.mainBodyCommands[0]) {
    return 'displayName' in object.mainBodyCommands[0];
  }
  return false;
}

function isInstanceOfShowCommandingSurfaceTreeData(
  object: ShowCommandingSurfaceData | ShowCommandingSurfaceTreeData
): object is ShowCommandingSurfaceTreeData {
  if (object.topBarCommands && object.topBarCommands[0]) {
    return 'displayName' in object.topBarCommands[0];
  }
  if (object.commandGroups && object.commandGroups[0]) {
    return 'displayName' in object.commandGroups[0];
  }
  return false;
}
